import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  VERSION_PATTERN,
  createNx5PackageRelease,
  fetchNx5Directories,
  fetchNx5Directory,
  fetchNx5Releases,
  formatBytes,
  getNx5OtaErrorMessage,
  relativePathForBrowserFile,
  removeNx5DirectoryFile,
  uploadNx5DirectoryFiles,
} from '../../helper/Nx5OtaHelper';
import ConfirmModal from '../DeviceDocuments/ConfirmModal';
import '../DeviceDocuments/DeviceDocuments.css';
import './Nx5Ota.css';

const emptyOs = { version: '', remote: '', commit: '' };

const CHANNEL_LABEL = {
  prod: 'Prod',
  test: 'Test',
  patch: 'Patch',
};

const VERSION_RULE = {
  prod: 'Prod raises the main number. Test and patch stay 0.',
  test: 'Test raises the test number. Main stays on the current prod. Patch stays 0.',
  patch: 'Patch raises the patch number. Main and test stay on the current line.',
};

export const Nx5ReleaseCreate = ({ channel }) => {
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const detailRequest = useRef(0);
  const releaseRequest = useRef(0);
  const [directories, setDirectories] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [releases, setReleases] = useState([]);
  const [nextVersion, setNextVersion] = useState('');
  const [osBase, setOsBase] = useState('1.0.0');
  const [os, setOs] = useState(emptyOs);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const loadDirectories = useCallback(() => fetchNx5Directories(channel).then((data) => {
    const list = Array.isArray(data?.directories) ? data.directories : [];
    setDirectories(list);
    setSelectedKey((current) => current || (list.find((item) => item.enabled) || list[0] || {}).key || '');
    return list;
  }), [channel]);

  const loadDetail = useCallback((directoryKey) => {
    if (!directoryKey) {
      setDetail(null);
      return Promise.resolve(null);
    }
    const requestId = detailRequest.current + 1;
    detailRequest.current = requestId;
    setDetailLoading(true);
    return fetchNx5Directory(directoryKey, channel)
      .then((data) => {
        if (detailRequest.current === requestId) setDetail(data);
        return data;
      })
      .finally(() => {
        if (detailRequest.current === requestId) setDetailLoading(false);
      });
  }, [channel]);

  const load = useCallback(() => {
    const requestId = releaseRequest.current + 1;
    releaseRequest.current = requestId;
    setNextVersion('');
    return Promise.all([
      loadDirectories(),
      fetchNx5Releases(channel).then((data) => {
        if (releaseRequest.current !== requestId) return;
        setReleases(Array.isArray(data?.releases) ? data.releases : []);
        setNextVersion(data?.nextVersion || '');
      }),
    ]).catch((err) => {
      if (releaseRequest.current !== requestId) return;
      setNotice({ tone: 'error', message: getNx5OtaErrorMessage(err) });
    });
  }, [channel, loadDirectories]);

  useEffect(() => {
    setDetail(null);
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedKey) return;
    loadDetail(selectedKey).catch((err) => {
      setNotice({ tone: 'error', message: getNx5OtaErrorMessage(err) });
    });
  }, [selectedKey, loadDetail]);

  const selected = directories.find((directory) => directory.key === selectedKey) || null;
  const files = Array.isArray(detail?.files) ? detail.files : [];

  const refreshSelected = () => Promise.all([
    loadDirectories(),
    loadDetail(selectedKey),
  ]);

  const startUpload = (fileList) => {
    const selectedFiles = Array.from(fileList || []);
    if (!selected || selectedFiles.length === 0) return;
    if (selected.type === 'file' && selectedFiles.length !== 1) {
      setNotice({ tone: 'error', message: `Upload ${selected.expectedFile}.` });
      return;
    }
    const payload = selectedFiles.map((file) => ({
      file,
      relativePath: selected.type === 'file' ? file.name : relativePathForBrowserFile(file),
    }));
    if (selected.type === 'file' && payload[0].relativePath !== selected.expectedFile) {
      setNotice({ tone: 'error', message: `This directory expects ${selected.expectedFile}.` });
      return;
    }
    setUploading(true);
    setProgress(0);
    uploadNx5DirectoryFiles(selected.key, payload, (event) => {
      if (!event.total) return;
      setProgress(Math.round((event.loaded / event.total) * 100));
    }, channel)
      .then((data) => {
        const count = Array.isArray(data?.files) ? data.files.length : payload.length;
        setNotice({ tone: 'success', message: `${count} file${count === 1 ? '' : 's'} saved in ${selected.name}.` });
        return refreshSelected();
      })
      .catch((err) => {
        setNotice({ tone: 'error', message: getNx5OtaErrorMessage(err) });
      })
      .finally(() => {
        setUploading(false);
        setProgress(0);
      });
  };

  const askDelete = (file) => {
    setConfirm({
      title: 'Remove file',
      message: `${file.relativePath} will be removed from this ${CHANNEL_LABEL[channel] || channel} working set.`,
      confirmLabel: 'Remove file',
      confirmTone: 'danger',
      onConfirm: () => {
        setConfirm((current) => ({ ...current, busy: true }));
        removeNx5DirectoryFile(file.id)
          .then(() => {
            setConfirm(null);
            return refreshSelected();
          })
          .catch((err) => {
            setConfirm(null);
            setNotice({ tone: 'error', message: getNx5OtaErrorMessage(err) });
          });
      },
    });
  };

  const create = () => {
    if (!VERSION_PATTERN.test(osBase)) {
      setNotice({ tone: 'error', message: 'Factory image version looks like 1.2.3' });
      return;
    }
    const osFilled = os.version || os.remote || os.commit;
    const body = { channel, osBase };
    if (osFilled) body.os = os;
    setBusy(true);
    createNx5PackageRelease(body)
      .then((data) => {
        const version = data?.release?.release || nextVersion;
        setNotice({ tone: 'success', message: `${CHANNEL_LABEL[channel] || channel} release ${version} created. Every application is ${version}.` });
        return fetchNx5Releases(channel).then((listed) => {
          setReleases(Array.isArray(listed?.releases) ? listed.releases : []);
          setNextVersion(listed?.nextVersion || '');
        });
      })
      .catch((err) => {
        setNotice({ tone: 'error', message: getNx5OtaErrorMessage(err) });
      })
      .finally(() => setBusy(false));
  };

  return (
    <section className="nx5-panel nx5-release" aria-label="Create release">
      <div className="nx5-block">
        <p className="nx5-section-label">Create {CHANNEL_LABEL[channel] || channel} release</p>
        <p className="nx5-hint">{VERSION_RULE[channel]} Every application in the release gets this version.</p>
        <div className="nx5-create-row">
          <label className="nx5-field">
            <span>Version</span>
            <input value={nextVersion} readOnly aria-label="Release version" />
          </label>
          <label className="nx5-field">
            <span>Factory image</span>
            <input value={osBase} onChange={(event) => setOsBase(event.target.value)} placeholder="1.0.0" aria-label="Factory image version" />
          </label>
          <button type="button" className="docs-btn docs-btn--primary nx5-create-btn" disabled={busy || uploading || !nextVersion} onClick={create}>
            {busy ? 'Working…' : 'Create release'}
          </button>
        </div>
      </div>
      <div className="nx5-block">
        <p className="nx5-section-label">OS overlay</p>
        <p className="nx5-hint">Comes from a git tag. Leave it blank when this release has no OS change.</p>
        <div className="nx5-os-row">
          <label className="nx5-field">
            <span>Tag</span>
            <input value={os.version} onChange={(event) => setOs((current) => ({ ...current, version: event.target.value }))} placeholder="1.4.2" aria-label="OS tag" />
          </label>
          <label className="nx5-field">
            <span>Git remote</span>
            <input value={os.remote} onChange={(event) => setOs((current) => ({ ...current, remote: event.target.value }))} placeholder="https:// or git@" aria-label="OS git remote" />
          </label>
          <label className="nx5-field">
            <span>Commit</span>
            <input value={os.commit} onChange={(event) => setOs((current) => ({ ...current, commit: event.target.value }))} placeholder="commit id" aria-label="OS commit" />
          </label>
        </div>
      </div>
      {notice && (
        <p className={`nx5-hint nx5-notice nx5-notice--${notice.tone}`}>{notice.message}</p>
      )}

      <div className="nx5-layout">
        <div className="docs-table-wrap nx5-directory-scroll">
          <table className="docs-table nx5-directory-table">
            <thead>
              <tr>
                <th>Directory</th>
                <th>Files</th>
              </tr>
            </thead>
            <tbody>
              {directories.length === 0 ? (
                <tr><td colSpan={2}>No package directories.</td></tr>
              ) : directories.map((directory) => (
                <tr key={directory.key} className={directory.key === selectedKey ? 'nx5-row--selected' : undefined}>
                  <td>
                    <button type="button" className="nx5-directory-button" onClick={() => setSelectedKey(directory.key)}>
                      <strong>{directory.name}</strong>
                      <span>{directory.key}</span>
                    </button>
                  </td>
                  <td>{directory.stagingCount || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="nx5-file-panel">
          {!selected ? (
            <p className="nx5-hint">Select a directory.</p>
          ) : (
            <>
              <div>
                <h4 className="nx5-detail-title">{selected.name}</h4>
                <p className="nx5-meta">
                  {selected.type === 'file' ? `File · expects ${selected.expectedFile}` : 'Folder'}
                  {selected.dest ? ` · ${selected.dest}` : ''}
                </p>
                {selected.summary ? <p className="nx5-meta">{selected.summary}</p> : null}
              </div>
              <div className="nx5-actions">
                <button type="button" className="docs-btn docs-btn--primary" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                  Upload {selected.type === 'file' ? 'file' : 'files'}
                </button>
                {selected.type === 'folder' ? (
                  <button type="button" className="docs-btn docs-btn--ghost" disabled={uploading} onClick={() => folderInputRef.current?.click()}>
                    Upload folder
                  </button>
                ) : null}
                {uploading ? (
                  <div className="nx5-progress" aria-label="Upload progress">
                    <span style={{ width: `${progress}%` }} />
                  </div>
                ) : null}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple={selected.type === 'folder'}
                  hidden
                  onChange={(event) => {
                    startUpload(event.target.files);
                    event.target.value = '';
                  }}
                />
                <input
                  ref={folderInputRef}
                  type="file"
                  multiple
                  hidden
                  {...{ webkitdirectory: '', directory: '' }}
                  onChange={(event) => {
                    startUpload(event.target.files);
                    event.target.value = '';
                  }}
                />
              </div>
              <div className="docs-table-wrap">
                <table className="docs-table">
                  <thead>
                    <tr>
                      <th>Path</th>
                      <th>Size</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {detailLoading ? <tr><td colSpan={3}>Loading files…</td></tr> : null}
                    {!detailLoading && files.length === 0 ? <tr><td colSpan={3}>No files uploaded yet.</td></tr> : null}
                    {!detailLoading && files.map((file) => (
                      <tr key={file.id}>
                        <td className="nx5-path">{file.relativePath}</td>
                        <td>{formatBytes(file.sizeBytes)}</td>
                        <td>
                          <button type="button" className="docs-btn docs-btn--danger docs-btn--small" onClick={() => askDelete(file)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="nx5-block">
        <p className="nx5-section-label">Releases</p>
      <div className="docs-table-wrap">
        <table className="docs-table">
          <thead>
            <tr>
              <th>Release</th>
              <th>Type</th>
              <th>Factory image</th>
              <th>Files</th>
            </tr>
          </thead>
          <tbody>
            {releases.length === 0 ? (
              <tr><td colSpan={4}>No {CHANNEL_LABEL[channel] || channel} release yet.</td></tr>
            ) : releases.map((item) => (
              <tr key={`${item.channel}:${item.release}`}>
                <td><strong>{item.release}</strong></td>
                <td>{CHANNEL_LABEL[item.channel] || item.channel}</td>
                <td>{item.osBase}</td>
                <td>{(item.components || []).map((component) => `${component.name} ${component.version}`).join(', ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>

      {confirm ? (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          confirmTone={confirm.confirmTone}
          busy={confirm.busy}
          onClose={() => setConfirm(null)}
          onConfirm={confirm.onConfirm}
        />
      ) : null}
    </section>
  );
};
